import dayjs from 'dayjs';
import { generateUUID } from '@utils/utils.tsx';
import type {
  ContractedCarrierForm,
  PriceIntervalForm,
  PriceLevelForm,
  PriceMatrix,
  PriceRowForm,
  PriceTableForm,
} from '@components/ringil3/Features/Contracted/types.ts';

/**
 * Contracted price table (RD-2978): levels shared by all carriers of a Linka, rows (manipulation unit + laden factor or
 * Výchozí), the plan of levels (periods, both days inclusive, never overlapping) and each carrier's matrix
 * level × row → price of one MU.
 *
 * Mirrors the backend for the parts it decides (level for a date, per MU the exact laden factor row else Výchozí);
 * the percentage fill and the level names are FE only — the backend stores whatever it gets.
 *
 * Dates are ISO days ('YYYY-MM-DD'), so string comparison orders them.
 */

const isSet = (value: number | null | undefined): value is number => value !== null && value !== undefined && !Number.isNaN(value);

export const round2 = (value: number) => Math.round(value * 100) / 100;

export const getCell = (matrix: PriceMatrix, levelKey: string, rowKey: string): number | null => {
  const value = matrix?.[levelKey]?.[rowKey];
  return isSet(value) ? value : null;
};

export const setCell = (matrix: PriceMatrix, levelKey: string, rowKey: string, value: number | null): PriceMatrix => ({
  ...(matrix ?? {}),
  [levelKey]: { ...(matrix?.[levelKey] ?? {}), [rowKey]: isSet(value) ? value : null },
});

/** PERCENTAGE: % of a level against the default one, from the positions ((index − default index) × step). Null otherwise. */
export const levelPercent = (table: PriceTableForm, levelKey: string): number | null => {
  if (table?.inputMode !== 'PERCENTAGE' || !isSet(table.stepPercent)) return null;
  const index = table.levels.findIndex(l => l.key === levelKey);
  const defaultIndex = table.levels.findIndex(l => l.key === table.defaultLevelKey);
  if (index < 0 || defaultIndex < 0) return null;
  return round2((index - defaultIndex) * table.stepPercent);
};

export const percentPrice = (base: number, percent: number) => round2(base * (1 + percent / 100));

/** PERCENTAGE: the default-level price of a row recomputes every other level of that row. */
const recomputeRow = (table: PriceTableForm, matrix: PriceMatrix, rowKey: string): PriceMatrix => {
  const base = getCell(matrix, table.defaultLevelKey, rowKey);
  return table.levels.reduce((acc, level) => {
    if (level.key === table.defaultLevelKey) return acc;
    const percent = levelPercent(table, level.key);
    return setCell(acc, level.key, rowKey, isSet(base) && isSet(percent) ? percentPrice(base, percent) : null);
  }, matrix);
};

/**
 * Edits one cell. In PERCENTAGE mode editing the default level's cell recomputes its whole row; any other cell is
 * an override that stays until its row's default price changes again.
 */
export const editCell = (table: PriceTableForm, matrix: PriceMatrix, levelKey: string, rowKey: string, value: number | null): PriceMatrix => {
  const next = setCell(matrix, levelKey, rowKey, value);
  if (table.inputMode === 'PERCENTAGE' && levelKey === table.defaultLevelKey) return recomputeRow(table, next, rowKey);
  return next;
};

/** After a structure change: cells of removed levels / rows are dropped; PERCENTAGE rows are recomputed. */
export const reconcileMatrix = (table: PriceTableForm, matrix: PriceMatrix): PriceMatrix => {
  let kept: PriceMatrix = {};
  table.levels.forEach(level => {
    table.rows.forEach(row => {
      const value = getCell(matrix, level.key, row.key);
      if (isSet(value)) kept = setCell(kept, level.key, row.key, value);
    });
  });
  if (table.inputMode !== 'PERCENTAGE') return kept;
  return table.rows.reduce((acc, row) => (isSet(getCell(acc, table.defaultLevelKey, row.key)) ? recomputeRow(table, acc, row.key) : acc), kept);
};

/**
 * Level names follow their position: the default level is "Úroveň 0", the ones above it -1, -2…, below it 1, 2…
 * Moving or removing a level renumbers the others.
 */
export const renumberLevels = (table: PriceTableForm, prefix: string): PriceTableForm => {
  const defaultIndex = Math.max(0, table.levels.findIndex(l => l.key === table.defaultLevelKey));
  return {
    ...table,
    defaultLevelKey: table.levels[defaultIndex]?.key ?? null,
    levels: table.levels.map((level, index) => ({ ...level, name: `${prefix} ${index - defaultIndex}` })),
  };
};

/** "FBU · LF 6" / "FBU · Výchozí". */
export const priceRowLabel = (row: Pick<PriceRowForm, 'cargoTemplateCode' | 'cargoTemplateName' | 'ladenFactor'>, defaultLabel: string) =>
  `${row.cargoTemplateName || row.cargoTemplateCode || '?'} · ${isSet(row.ladenFactor) ? `LF ${row.ladenFactor}` : defaultLabel}`;

/** Level whose period contains [date], or null — a gap of the plan ("Nezadáno"). */
export const levelForDate = (table: PriceTableForm, date: string): PriceLevelForm | null => {
  if (!table || !date) return null;
  const interval = table.intervals.find(i => i.validFrom <= date && date <= i.validTo);
  return interval ? (table.levels.find(l => l.key === interval.levelKey) ?? null) : null;
};

/** Keys of periods sharing a day with another period (both ends inclusive). */
export const overlappingIntervalKeys = (intervals: PriceIntervalForm[]): Set<string> => {
  const result = new Set<string>();
  const complete = (intervals ?? []).filter(i => i.validFrom && i.validTo);
  complete.forEach(a =>
    complete.forEach(b => {
      if (a.key !== b.key && a.validFrom <= b.validTo && b.validFrom <= a.validTo) result.add(a.key);
    }),
  );
  return result;
};

export interface ResolvedMuPrice {
  /** Unit price, null = the carrier has no price for this MU on the level. */
  price: number | null;
  /** Laden factor of the row the price comes from; null = Výchozí. */
  ladenFactor: number | null;
}

export interface ResolvedContractedPrice {
  level: PriceLevelForm | null;
  /** Per carrier id: per MU of the shipment the price of the applicable row. */
  byCarrier: Record<string, Record<string, ResolvedMuPrice>>;
}

/** Row of [muCode] that prices the shipment: the exact laden factor, else Výchozí (same rule as the backend). */
export const applicableRow = (table: PriceTableForm, muCode: string, shipmentLadenFactor?: number | null): PriceRowForm | null => {
  const ofMu = (table?.rows ?? []).filter(r => r.cargoTemplateCode === muCode);
  if (isSet(shipmentLadenFactor)) {
    const exact = ofMu.find(r => isSet(r.ladenFactor) && Number(r.ladenFactor) === Number(shipmentLadenFactor));
    if (exact) return exact;
  }
  return ofMu.find(r => !isSet(r.ladenFactor)) ?? null;
};

/** Same resolution as the backend at distribution create: level on the deadline day, per MU exact LF row else Výchozí. */
export const resolveContractedPrices = (
  table: PriceTableForm,
  carriers: ContractedCarrierForm[],
  deadlineDate: string,
  shipmentLadenFactor: number | null | undefined,
  muCodes: string[],
): ResolvedContractedPrice => {
  const level = levelForDate(table, deadlineDate);
  const byCarrier: Record<string, Record<string, ResolvedMuPrice>> = {};
  (carriers ?? []).forEach(c => {
    byCarrier[c.carrierId] = {};
    muCodes.forEach(mu => {
      const row = applicableRow(table, mu, shipmentLadenFactor);
      byCarrier[c.carrierId][mu] = { price: level && row ? getCell(c.prices, level.key, row.key) : null, ladenFactor: row?.ladenFactor ?? null };
    });
  });
  return { level, byCarrier };
};

/** MUs the carrier has no price for on the resolved level. */
export const missingMuPrices = (resolved: ResolvedContractedPrice, carrierId: string): string[] =>
  Object.entries(resolved.byCarrier[carrierId] ?? {})
    .filter(([, p]) => !isSet(p.price))
    .map(([mu]) => mu);

/** Σ units × unit price; null when a MU with units has no price. */
export const contractedTotal = (resolved: ResolvedContractedPrice, carrierId: string, unitsByMu: Record<string, number>): number | null => {
  let total = 0;
  for (const [mu, units] of Object.entries(unitsByMu ?? {})) {
    if (!(units > 0)) continue;
    const price = resolved.byCarrier[carrierId]?.[mu]?.price;
    if (!isSet(price)) return null;
    total += units * price;
  }
  return round2(total);
};

/** Laden factors the table has rows for, the "1 vozidlo veze" options. */
export const tableLadenFactors = (table: PriceTableForm): number[] =>
  Array.from(new Set((table?.rows ?? []).map(r => r.ladenFactor).filter(isSet).map(Number))).sort((a, b) => a - b);

const daysBetweenInclusive = (from: string, to: string) => dayjs(to).diff(dayjs(from), 'day') + 1;
const addDays = (date: string, days: number) => dayjs(date).add(days, 'day').format('YYYY-MM-DD');

export interface TimelineRow {
  key: string;
  /** null = a gap of the plan ("Nezadáno") */
  levelKey: string | null;
  levelName: string | null;
  from: string;
  to: string;
  current: boolean;
  /** Days left of the current row (today included), otherwise its length. */
  days: number;
}

/**
 * Rows of the plan from today on: the periods not over yet and the gaps between them (and before the first one when
 * today is not planned). Nothing after the last period — the plan simply ends there.
 */
export const timelineRows = (table: PriceTableForm, today: string): TimelineRow[] => {
  const upcoming = [...(table?.intervals ?? [])].filter(i => i.validFrom && i.validTo && i.validTo >= today).sort((a, b) => a.validFrom.localeCompare(b.validFrom));
  const rows: TimelineRow[] = [];
  let cursor = today;
  upcoming.forEach(interval => {
    if (interval.validFrom > cursor) {
      const gapTo = addDays(interval.validFrom, -1);
      rows.push({
        key: `gap-${cursor}`,
        levelKey: null,
        levelName: null,
        from: cursor,
        to: gapTo,
        current: cursor === today,
        days: daysBetweenInclusive(cursor, gapTo),
      });
    }
    const current = interval.validFrom <= today && today <= interval.validTo;
    rows.push({
      key: interval.key,
      levelKey: interval.levelKey,
      levelName: table.levels.find(l => l.key === interval.levelKey)?.name ?? null,
      from: interval.validFrom,
      to: interval.validTo,
      current,
      days: current ? daysBetweenInclusive(today, interval.validTo) : daysBetweenInclusive(interval.validFrom, interval.validTo),
    });
    if (interval.validTo >= cursor) cursor = addDays(interval.validTo, 1);
  });
  return rows;
};

/** The whole plan as CSV (level;from;to), past and future periods alike, oldest first. */
export const planCsv = (table: PriceTableForm, header: [string, string, string]): string => {
  const periods = (table?.intervals ?? []).filter(i => i.validFrom && i.validTo).sort((a, b) => a.validFrom.localeCompare(b.validFrom));
  const levelName = (key: string) => table.levels.find(l => l.key === key)?.name ?? '';
  const escape = (value: string) => (/[;"\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  return [header.map(escape).join(';'), ...periods.map(i => [levelName(i.levelKey), i.validFrom, i.validTo].map(escape).join(';'))].join('\n');
};

export const newPriceLevel = (name: string): PriceLevelForm => ({ key: generateUUID(), name });

export const newPriceInterval = (levelKey: string = null): PriceIntervalForm => ({ key: generateUUID(), levelKey, validFrom: null, validTo: null });

export const newPriceRow = (cargoTemplateCode: string = null, ladenFactor: number = null): PriceRowForm => ({ key: generateUUID(), cargoTemplateCode, ladenFactor });

/** A fresh table: one level "Úroveň 0", a Výchozí row of [cargoTemplateCode] when given, no plan. */
export const newPriceTable = (currency: string, levelPrefix: string, cargoTemplateCode: string = null): PriceTableForm => {
  const level = newPriceLevel(`${levelPrefix} 0`);
  return {
    inputMode: 'BASIC',
    levels: [level],
    defaultLevelKey: level.key,
    stepPercent: null,
    rows: cargoTemplateCode ? [newPriceRow(cargoTemplateCode)] : [],
    currency,
    intervals: [],
  };
};

/** First problem of the shared price table, as a message; null = fine. Matrix completeness is checked per shipment. */
export const priceTableIssue = (table: PriceTableForm | undefined, tr: (key: string, defaultText?: string) => string): string | null => {
  if (!table || table.levels.length === 0) return tr('PriceTable.levelsRequired', 'Zadejte alespoň jednu cenovou úroveň');
  if (table.levels.some(l => !l.name?.trim())) return tr('PriceTable.levelNameRequired', 'Každá úroveň musí mít název');
  const names = table.levels.map(l => l.name.trim().toLowerCase());
  if (new Set(names).size !== names.length) return tr('PriceTable.levelNameDuplicate', 'Názvy úrovní se nesmí opakovat');
  if (table.rows.some(r => !r.cargoTemplateCode)) return tr('PriceTable.rowMuRequired', 'U každého řádku vyberte manipulační jednotku');
  const rowKeys = table.rows.map(r => `${r.cargoTemplateCode}|${isSet(r.ladenFactor) ? Number(r.ladenFactor) : ''}`);
  if (new Set(rowKeys).size !== rowKeys.length) return tr('PriceTable.rowDuplicate', 'Kombinace manipulační jednotky a ladenfaktoru se opakuje');
  if (!table.currency) return tr('PriceTable.currencyRequired', 'Vyberte měnu');
  if (table.inputMode === 'PERCENTAGE' && (!table.defaultLevelKey || table.stepPercent === null || table.stepPercent === undefined)) {
    return tr('PriceTable.percentageRequired', 'Pro procentuální zadání vyberte výchozí úroveň a rozdíl mezi úrovněmi');
  }
  if (table.intervals.some(i => !i.levelKey || !i.validFrom || !i.validTo)) return tr('PricePlan.incomplete', 'Každé období musí mít úroveň a obě data');
  if (table.intervals.some(i => i.validFrom > i.validTo)) return tr('PricePlan.fromAfterTo', 'Období musí začínat dříve, než končí');
  if (overlappingIntervalKeys(table.intervals).size > 0) return tr('PricePlan.overlap', 'Období se nesmí překrývat');
  return null;
};
